import { useEffect, useState, forwardRef, useRef } from "react";
import { MdCast, MdAirplay } from "react-icons/md";

/* global cast, chrome */

/**
 * Botón Cast reutilizable que inicializa el framework de Google Cast
 * y envía una carga de audio cuando el usuario selecciona un dispositivo.
 *
 * Props:
 *  - streamUrl: url del flujo de audio (mp3)
 *  - title: título de la pista
 *  - artist: artista
 *  - album: nombre del álbum / estación
 *  - image: url de la carátula
 *  - className: clases adicionales opcionales para estilos
 *  - audio: elemento de audio para AirPlay
 */
const CastButton = forwardRef(
  (
    { streamUrl, title, artist, album, image, className = "", audio = null },
    ref
  ) => {
    const [castReady, setCastReady] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);

    // Detectar Safari (desktop o móvil) usando UA simple
    const isSafari = (() => {
      if (typeof navigator === "undefined") return false;
      const ua = navigator.userAgent.toLowerCase();
      return (
        ua.includes("safari") &&
        !ua.includes("chrome") &&
        !ua.includes("crios") &&
        !ua.includes("android")
      );
    })();

    // Referencia interna a un elemento <audio> oculto (Safari)
    const internalAudioRef = useRef(null);

    // Se evalúa dinámicamente si AirPlay está soportado
    const [airPlaySupported, setAirPlaySupported] = useState(false);

    useEffect(() => {
      // Si usamos AirPlay, no es necesario inicializar Google Cast
      if (airPlaySupported) {
        // Escuchar cambios de estado de AirPlay para reflejar transmisión activa
        const el = internalAudioRef.current || audio;

        const updateAirplayState = () => {
          try {
            setIsStreaming(
              el &&
                typeof el.webkitCurrentPlaybackTargetIsWireless ===
                  "function" &&
                el.webkitCurrentPlaybackTargetIsWireless()
            );
          } catch (e) {
            // Silenciar errores si la función no está disponible
          }
        };

        if (el) {
          el.addEventListener(
            "webkitcurrentplaybacktargetiswirelesschanged",
            updateAirplayState
          );
          // Estado inicial
          updateAirplayState();
        }

        return () => {
          if (el) {
            el.removeEventListener(
              "webkitcurrentplaybacktargetiswirelesschanged",
              updateAirplayState
            );
          }
        };
      }
    }, [airPlaySupported, audio]);

    useEffect(() => {
      // Si AirPlay está soportado, saltar inicialización de Cast.
      if (airPlaySupported) {
        setCastReady(true);
        return;
      }

      if (window.__CAST_SDK_LOADED__) {
        setCastReady(true);
        checkSessionStatus();
        return;
      }

      // Definir el callback global ANTES de inyectar el script
      window.__onGCastApiAvailable = function (isAvailable) {
        if (isAvailable) {
          // Configurar el contexto Cast
          if (typeof cast !== "undefined" && cast.framework) {
            const context = cast.framework.CastContext.getInstance();
            context.setOptions({
              receiverApplicationId:
                typeof chrome !== "undefined" &&
                chrome.cast &&
                chrome.cast.media
                  ? chrome.cast.media.DEFAULT_MEDIA_RECEIVER_APP_ID
                  : "CC1AD845", // ID por defecto si chrome no está disponible
              autoJoinPolicy: chrome.cast.AutoJoinPolicy.ORIGIN_SCOPED,
            });
            window.__CAST_SDK_LOADED__ = true;
            setCastReady(true);

            checkSessionStatus();

            context.addEventListener(
              cast.framework.CastContextEventType.SESSION_STATE_CHANGED,
              (event) => {
                const isActive =
                  event.sessionState ===
                    cast.framework.SessionState.SESSION_STARTED ||
                  event.sessionState ===
                    cast.framework.SessionState.SESSION_RESUMED;
                setIsStreaming(isActive);
              }
            );
          }
        }
      };

      // Insertar script único
      if (!document.getElementById("google-cast-sdk")) {
        const script = document.createElement("script");
        script.id = "google-cast-sdk";
        script.src =
          "https://www.gstatic.com/cv/js/sender/v1/cast_sender.js?loadCastFramework=1";
        script.async = true;
        document.body.appendChild(script);
      }
    }, [airPlaySupported]);

    const checkSessionStatus = () => {
      if (!window.__CAST_SDK_LOADED__) return;

      try {
        if (typeof cast === "undefined" || !cast.framework) return;

        const context = cast.framework.CastContext.getInstance();
        const session = context.getCurrentSession();

        setIsStreaming(!!session);
      } catch (error) {
        console.error("Error al verificar estado de la sesión:", error);
      }
    };

    // Carga la media en el receptor Cast seleccionado
    const loadMedia = () => {
      if (typeof cast === "undefined" || !cast.framework) return;

      const session =
        cast.framework.CastContext.getInstance().getCurrentSession();
      if (!session) return;

      try {
        if (typeof chrome === "undefined" || !chrome.cast || !chrome.cast.media)
          return;

        const mediaInfo = new chrome.cast.media.MediaInfo(
          streamUrl,
          "audio/mpeg"
        );
        const metadata = new chrome.cast.media.MusicTrackMediaMetadata();
        metadata.title = title || "Audio en vivo";
        metadata.artist = artist || "";
        metadata.albumName = album || "";
        if (image) {
          metadata.images = [{ url: image }];
        }
        mediaInfo.metadata = metadata;
        mediaInfo.streamType = chrome.cast.media.StreamType.BUFFERED;

        const request = new chrome.cast.media.LoadRequest(mediaInfo);
        session.loadMedia(request).catch((error) => {
          // eslint-disable-next-line no-console
          console.error("Error al enviar el medio al dispositivo Cast", error);
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error al preparar el medio para Cast", error);
      }
    };

    const endCurrentSession = () => {
      try {
        if (typeof cast === "undefined" || !cast.framework) return;

        const context = cast.framework.CastContext.getInstance();
        const session = context.getCurrentSession();

        if (session) {
          session.endSession(true);
          setIsStreaming(false);
        }
      } catch (error) {
        console.error("Error al terminar la sesión de Cast:", error);
      }
    };

    // Maneja el clic del botón
    const handleClick = () => {
      if (airPlaySupported) {
        // Flujo AirPlay
        const el = internalAudioRef.current || audio;
        if (!el) return;
        try {
          el.webkitShowPlaybackTargetPicker();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Error al mostrar el selector de AirPlay", error);
        }
        return;
      }

      // Google Cast flow (no Safari)
      if (!castReady) {
        return;
      }

      if (isStreaming) {
        endCurrentSession();
        return;
      }

      if (typeof cast === "undefined" || !cast.framework) return;

      const context = cast.framework.CastContext.getInstance();
      if (!context.getCurrentSession()) {
        context
          .requestSession()
          .then(() => {
            loadMedia();
            setIsStreaming(true);
          })
          .catch((err) => {
            // eslint-disable-next-line no-console
            console.error("Error al iniciar la sesión de Cast", err);
          });
      } else {
        loadMedia();
        setIsStreaming(true);
      }
    };

    // Crea un elemento de audio oculto en Safari (si es necesario) y determina soporte de AirPlay
    useEffect(() => {
      let el = audio;

      // Crear audio oculto cuando no se provee uno desde el padre
      if (isSafari && !el) {
        const a = document.createElement("audio");
        a.src = streamUrl;
        a.preload = "none";
        a.style.display = "none";
        document.body.appendChild(a);
        internalAudioRef.current = a;
        el = a;
      }

      const supported =
        isSafari &&
        el &&
        typeof el.webkitShowPlaybackTargetPicker === "function";

      setAirPlaySupported(supported);

      if (supported) {
        setCastReady(true);
      }

      return () => {
        if (internalAudioRef.current) {
          internalAudioRef.current.pause();
          internalAudioRef.current.src = "";
          internalAudioRef.current.remove();
          internalAudioRef.current = null;
        }
      };
    }, [audio, isSafari, streamUrl]);

    return (
      <div
        className={`CastButton ${className} ${
          isStreaming ? "active" : ""
        }`.trim()}
        onClick={handleClick}
        ref={ref}
        style={{ cursor: castReady ? "pointer" : "not-allowed" }}
        title={
          airPlaySupported
            ? "Transmitir vía AirPlay"
            : castReady
            ? isStreaming
              ? "Detener transmisión"
              : "Enviar a dispositivo"
            : "Cast no disponible"
        }
      >
        {airPlaySupported ? (
          <MdAirplay color="#FFFFFF" size={24} />
        ) : (
          <MdCast color="#FFFFFF" size={24} />
        )}
      </div>
    );
  }
);

export default CastButton;
